import { type ReactNode, useMemo, useState } from 'react';
import { mayRaiseIssueOn } from '@d-orca/shared/client/issues';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useAppDispatch } from '@store/hooks';
import { openCreateDialogForTask, openEditDialog } from '@store/slices/dialog.slice';
import { typeMeta } from '../typeRegistry';
import { CriticalPathTag } from '../CriticalPathTag';
import { ChecklistChip } from '@components/ui/ChecklistChip/ChecklistChip';
import { MilestoneMarker } from '../MilestoneMarker';
import { lateDaysOf, rungOf, type IPromiseFacts } from '../WorkPlan/workPlanModel';
import { LadderChip } from '../../SchedulerPage/components/LadderChip/LadderChip';
import { ActivityLines } from './ActivityLines';
import { RiskBadges } from './RiskBadges';
import { StatusPill } from './StatusPill';
import { CardBulb } from './CardBulb';
import { CardMenu } from './CardMenu';
import { cardOpenHandlers } from './cardOpenHandlers';
import { IssueMarks } from './IssueMarks';
import { StatusDot } from '@components/ui/StatusDot/StatusDot';
import { useDeleteIssue, useSiteProfessions } from '@components/Pages/IssuesPage/hooks';
import { IssueStatusControl } from '../IssueStatus/IssueStatusControl';
import { Modal } from '@components/ui/Modal/Modal';
import { Button } from '@components/ui/Button/Button';
import { tradeLabel } from '@utils/tradeLabel';
import { ChatBubbleIcon } from '@components/Pages/IssuesPage/Board/icons';
import { ProfessionChip } from '@components/ui/ProfessionChip/ProfessionChip';
import { PersonIcon, ClockIcon, MapPinIcon, CalendarIcon } from '@utils/icons';
import type { IListItem } from '../model/listItem';
import { toDay } from '../model/listItem';
import { projectToday } from '../activitiesFilter';
import { deriveDisplayStatus } from '../model/displayStatus';
import { formatDateRange } from '../model/formatDateRange';
import { formatIssueLocations, resolveProfessionChip } from '../model/issueDisplay';
import styles from './cardList.module.css';
import bulbStyles from './CardBulb.module.css';
import { useSelector } from 'react-redux';
import { selectSiteTz } from '@store/selectors/site.selectors';
import { OWNS_ERROR_FEEDBACK } from '@d-orca/shared/client/hooks';

export interface IActivityCardProps {
  item: IListItem;
  onOpen: (item: IListItem) => void;
  unseen?: boolean;
  unreadCount?: number;
  /** An activity's floor, areas done and work time — what the Work Plan's
   *  bar carries. Absent for an issue. */
  facts?: IPromiseFacts;
  /** The viewer holds `issues.manage`. Without it an issue's star, shield and
   *  kebab (edit, delete) and a task's Raise issue menu are not rendered; the
   *  star and shield remain as non-interactive icons when set, and the status
   *  control offers the contributor's moves (submit for review, undo). */
  canManageIssues: boolean;
}

const ageDays = (dateStr: string | undefined, todayStr: string): number | null => {
  if (!dateStr) return null;
  const d = Date.parse(dateStr);
  if (!isFinite(d)) return null;
  return Math.floor((Date.parse(todayStr) - d) / 86_400_000);
};

export function ActivityCard({ item, onOpen, unseen = false, unreadCount = 0, facts, canManageIssues }: IActivityCardProps) {
  /* The SITE's today drives the status derivation and the year elision below.
     The list this card lives in is empty until the working site resolves, so
     `tz` is present in practice — the guard keeps that honest rather than
     dating the card in a guessed zone. */
  const tz = useSelector(selectSiteTz);
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const { siteId } = useParams<{ siteId: string }>();
  // A failed delete is reported inline, under its button.
  const del = useDeleteIssue(siteId ?? null, OWNS_ERROR_FEEDBACK);
  const professionsQ = useSiteProfessions(siteId ?? null);
  // Delete is a hard delete upstream — always confirmed via modal, never
  // fired from the menu click itself.
  const [confirmDelete, setConfirmDelete] = useState(false);

  const meta = typeMeta(item.kind);
  const toneClass = styles[`tone_${meta.tone}`] ?? '';
  const issue = item.kind === 'issue' ? item.raw.issue : undefined;

  // Memoize the profession chip resolution — avoids a catalog .find() on every render.
  const chip = useMemo(
    () =>
      resolveProfessionChip(
        issue?.profession,
        professionsQ.data?.professions ?? [],
        (id) => tradeLabel(t, id),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [issue?.profession, professionsQ.data, i18n.language],
  );

  // Every hook is above this line — the guard has to follow them all. The card
  // only ever renders for an item from the unified list, which stays empty until
  // the working site resolves, so this is a type-level honesty guard rather than
  // a state users reach.
  if (!tz) return null;

  const today = projectToday(tz);
  // An activity item carries its committed task — the ladder reads it.
  const task = item.raw.activity;
  const rung = task && rungOf(item);

  const currentYear = Number(today.slice(0, 4));
  const dateLabel = formatDateRange(item.start, item.end, i18n.language, currentYear);

  // Cheap date/age derivations — React Compiler memoizes these automatically.
  const createDateStr = toDay(issue?.createDate, tz);
  const createdAge = ageDays(createDateStr, today);

  const showDot = unseen || unreadCount > 0;
  const cappedUnread = unreadCount > 99 ? '99+' : String(unreadCount);

  return (
    <div className={styles.card} {...cardOpenHandlers(item.title, () => onOpen(item))}>
      <span className={styles.typeIconWrapper}>
        {showDot && <StatusDot color="var(--red)" size={8} />}
        <span className={`${styles.typeIcon} ${toneClass}`} aria-hidden="true">
          {meta.icon(16)}
        </span>
      </span>

      <div className={styles.titleRow}>
        <span className={styles.title}>{item.title}</span>
        {item.isMilestone && <MilestoneMarker size={16} />}
        {item.isOnCriticalPath && <CriticalPathTag />}
        <ChecklistChip checklist={item.raw.activity?.checklist} />
        {issue && <IssueMarks issue={issue} canManage={canManageIssues} siteId={siteId ?? null} />}
        {unreadCount > 0 && (
          <span className={styles.unreadLabel}>
            {t('activities.unreadComments', { count: unreadCount, display: cappedUnread })}
          </span>
        )}
      </div>

      <div className={styles.statusRow}>
        {/* An activity wears the lifecycle ladder, as the Work Plan and the
            Scheduler do: delayed and overdue in the late red with their
            days late, overdue flagged. An issue keeps its review states. */}
        {task && rung ? (
          <LadderChip rung={rung} lateDays={lateDaysOf(task, rung, today)} />
        ) : (
          <StatusPill status={deriveDisplayStatus(item, today)} />
        )}
        {/* Raising an issue creates one — `issues.manage`. Only a committed
            FIELD task has an activity in Trusstor to link an issue to:
            `activityStatus` is the sign it was committed, and an administrative
            task is committed in D-ORCA alone. The menu's one item is its only
            reason to exist, so without it there is no menu at all. */}
        {canManageIssues && task && mayRaiseIssueOn(task, item.activityStatus) && (
          <CardMenu
            label={t('activities.card.activityActions')}
            items={[{
              label: t('activities.card.raiseIssue'),
              onSelect: () => dispatch(openCreateDialogForTask({ id: task.id, title: item.title })),
            }]}
          />
        )}
        {item.kind === 'issue' && issue && canManageIssues && (
        <>
        <CardMenu
          label={t('activities.card.issueActions')}
          items={[
            { label: t('activities.card.editIssue'), onSelect: () => dispatch(openEditDialog(issue._id)) },
            { label: t('activities.card.deleteIssue'), onSelect: () => setConfirmDelete(true), danger: true },
          ]}
        />
        {/* The Modal portals to document.body, but React synthetic events
            still bubble through the JSX tree — stop them here so clicks
            inside the dialog don't also open the card's drawer. */}
        <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
          <Modal
            open={confirmDelete}
            onClose={() => setConfirmDelete(false)}
            title={t('activities.card.deleteConfirmTitle')}
            description={t('activities.card.deleteConfirmBody')}
            size="sm"
            footer={
              <>
                <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(false)}>
                  {t('common.cancel')}
                </Button>
                <Button
                  variant="dangerSolid"
                  size="sm"
                  disabled={del.isPending}
                  onClick={() =>
                    del.mutate(
                      { issueId: issue._id },
                      { onSuccess: () => setConfirmDelete(false) },
                    )
                  }
                >
                  {t('activities.card.deleteConfirm')}
                </Button>
              </>
            }
          >
            {del.isError && <p className={styles.deleteError}>{t('activities.card.deleteIssueError')}</p>}
          </Modal>
        </span>
        </>
        )}
      </div>

      <div className={styles.body}>
        {task ? (
          <ActivityLines task={task} dateLabel={dateLabel} facts={facts} />
        ) : (
          (() => {
            const dueDateLabel = formatDateRange(undefined, item.end, i18n.language, currentYear);
            const locationLabel = issue?.locations ? formatIssueLocations(issue.locations) : '';

            const labelWithIcon = (icon: ReactNode, label: string) => (
              <dt className={bulbStyles.rowLabel}>
                <span className={bulbStyles.rowLabelIcon} aria-hidden="true">{icon}</span>
                {label}
              </dt>
            );

            return (
              <>
                {/* Key fields live OUTSIDE the bulb, as a meta row under the title. */}
                <div className={styles.meta}>
                  {dueDateLabel && (
                    <span className={styles.metaItem}>
                      <ClockIcon size={13} />
                      {dueDateLabel}
                    </span>
                  )}
                  {locationLabel && (
                    <span className={styles.metaItem}>
                      <MapPinIcon size={13} />
                      {locationLabel}
                    </span>
                  )}
                  {chip && <ProfessionChip chip={chip} showContractor={false} />}
                </div>

                <CardBulb label={t('activities.bulb.details')}>
                  <div className={bulbStyles.split}>
                    {/* Left: assignee + created */}
                    <dl className={bulbStyles.rows}>
                      {item.assigneeName && (
                        <>
                          {labelWithIcon(<PersonIcon size={12} />, t('activities.drawer.assignee'))}
                          <dd className={bulbStyles.rowValue}>{item.assigneeName}</dd>
                        </>
                      )}
                      {createDateStr && (
                        <>
                          {labelWithIcon(<CalendarIcon size={12} />, t('activities.bulb.created'))}
                          <dd className={bulbStyles.rowValue}>
                            {formatDateRange(undefined, createDateStr, i18n.language, currentYear)}
                            {createdAge !== null && t('activities.bulb.daysAgo', { n: createdAge })}
                          </dd>
                        </>
                      )}
                    </dl>
                    {/* Right: comments count */}
                    <dl className={bulbStyles.rows}>
                      {labelWithIcon(<ChatBubbleIcon size={12} />, t('activities.bulb.comments'))}
                      <dd className={bulbStyles.rowValue}>{item.commentCount}</dd>
                    </dl>
                  </div>
                </CardBulb>
              </>
            );
          })()
        )}
      </div>

      <div className={styles.foot}>
        <RiskBadges onTime={item.onTimeRisk} safety={item.safety} showReasons relatedTasks={item.relatedTasks} tz={tz} />

        {issue && (
          <IssueStatusControl
            issue={issue}
            siteId={siteId ?? null}
            canManage={canManageIssues}
            className={styles.statusAction}
          />
        )}
      </div>
    </div>
  );
}
